import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  User,
  onAuthStateChanged,
  signOut,
  getIdTokenResult,
  updateProfile,
} from 'firebase/auth';
import { auth } from '../lib/firebase';
import {
  startNotificationsRealtime,
  stopNotificationsRealtime,
} from '../lib/notificationsRealtime';

type Role = 'admin' | 'agent' | 'client' | 'candidate';
type Mode = 'light' | 'dark';

type OrganizationRole = 'owner' | 'member';

type OrganizationPermissions = {
  inviteMembers: boolean;
  createTickets: boolean;
  manageBilling: boolean;
  manageSubscription: boolean;
  manageOrgProfile: boolean;
};

type OrganizationContext = {
  orgId: string;
  orgName: string;
  organizationType: 'individual' | 'business';
  displayLabel: string;
  businessName: string | null;
  logoURL: string | null;
  avatarURL: string | null;
  role: OrganizationRole;
  isPrimaryAccountHolder: boolean;
  isActive: boolean;
  permissions: OrganizationPermissions;
};

interface AuthCtx {
  user: User | null | undefined;
  role: Role | null;
  activeOrgId: string | null;
  organizations: OrganizationContext[];
  setActiveOrg: (orgId: string) => Promise<void>;
  orgId: string | null;
  hasOrganizationMembership: boolean;
  isPaid: boolean;
  businessProfileComplete: boolean;
  isPrimaryAccountHolder: boolean;
  requiresInitialPasswordSetup: boolean;
  isBanned: boolean;
  banReason: string | null;
  theme: Mode;
  loading: boolean;
  refreshRole: () => Promise<void>;
  setTheme: (m: Mode) => Promise<void>;
  logout: () => Promise<void>;
}

const Context = createContext<AuthCtx>({
  user: undefined,
  role: null,
  activeOrgId: null,
  organizations: [],
  setActiveOrg: async () => {},
  orgId: null,
  hasOrganizationMembership: false,
  isPaid: false,
  businessProfileComplete: false,
  isPrimaryAccountHolder: false,
  requiresInitialPasswordSetup: false,
  isBanned: false,
  banReason: null,
  theme: 'light',
  loading: true,
  refreshRole: async () => {},
  setTheme: async () => {},
  logout: async () => {},
});

const API_BASE = (process.env.REACT_APP_API_BASE ?? '').trim();

async function ensureUserDoc(user: User): Promise<void> {
  try {
    const token = await user.getIdToken();
    await fetch(`${API_BASE}/api/users`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: user.email ?? null,
        displayName: user.displayName ?? null,
        photoURL: user.photoURL ?? null,
      }),
    });
  } catch (err) {
    console.error('Failed to create user doc', err);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>();
  const [role, setRole] = useState<Role | null>(null);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationContext[]>([]);
  const [hasOrganizationMembership, setHasOrganizationMembership] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [businessProfileComplete, setBusinessProfileComplete] = useState(false);
  const [isPrimaryAccountHolder, setIsPrimaryAccountHolder] = useState(false);
  const [requiresInitialPasswordSetup, setRequiresInitialPasswordSetup] = useState(false);
  const [isBanned, setIsBanned] = useState(false);
  const [banReason, setBanReason] = useState<string | null>(null);
  const [theme, setThemeState] = useState<Mode>('light');
  const [loading, setLoad] = useState(true);

  const fetchProfile = async (u: User | null) => {
    if (!u) {
      setRole(null);
      setActiveOrgId(null);
      setOrganizations([]);
      setHasOrganizationMembership(false);
      setIsPaid(false);
      setBusinessProfileComplete(false);
      setIsPrimaryAccountHolder(false);
      setRequiresInitialPasswordSetup(false);
      setIsBanned(false);
      setBanReason(null);
      setThemeState('light');
      setLoad(false);
      return;
    }

    try {
      const claims = await getIdTokenResult(u);
      const claimsPaid = Boolean(claims.claims?.stripeRole || claims.claims?.paid);

      const token = await u.getIdToken();
      const res = await fetch(`${API_BASE}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        const data = await res.json().catch(() => null);
        const reason =
          typeof data?.message === 'string' && data.message.trim()
            ? data.message.trim()
            : 'Your account is locked. Please contact an administrator.';
        setRole(null);
        setActiveOrgId(null);
        setOrganizations([]);
        setHasOrganizationMembership(false);
        setIsPaid(false);
        setBusinessProfileComplete(false);
        setIsPrimaryAccountHolder(false);
        setRequiresInitialPasswordSetup(false);
        setIsBanned(true);
        setBanReason(reason);
        setThemeState('light');
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const banned = Boolean(data?.banned);
      if (banned) {
        const reason =
          typeof data?.banReason === 'string' && data.banReason.trim()
            ? data.banReason.trim()
            : typeof data?.message === 'string' && data.message.trim()
            ? data.message.trim()
            : 'Your account is locked. Please contact an administrator.';
        setRole(null);
        setActiveOrgId(null);
        setOrganizations([]);
        setHasOrganizationMembership(false);
        setIsPaid(false);
        setBusinessProfileComplete(false);
        setIsPrimaryAccountHolder(false);
        setRequiresInitialPasswordSetup(false);
        setIsBanned(true);
        setBanReason(reason);
        setThemeState('light');
        return;
      }

      const r = data?.role as Role | undefined;
      const tmode = data?.theme as Mode | undefined;
      const status = String(data?.subscriptionStatus || '').toLowerCase();
      const profilePaid =
        status === 'active' || status === 'trialing' || Boolean(data?.planId);
      const backendPhoto =
        typeof data?.photoURL === 'string' ? data.photoURL.trim() : '';
      if (backendPhoto && backendPhoto !== (u.photoURL ?? '')) {
        updateProfile(u, { photoURL: backendPhoto }).catch((err) => {
          console.warn('Failed to sync auth profile photoURL', err);
        });
      }

      setIsBanned(false);
      setBanReason(null);
      setIsPaid(claimsPaid || profilePaid);

      const normalizeText = (value: unknown): string | null => {
        if (typeof value !== 'string') return null;
        const trimmed = value.trim();
        return trimmed || null;
      };

      const organizationsRaw = Array.isArray(data?.organizations)
        ? data.organizations
        : [];

      const normalizedOrganizations: OrganizationContext[] = organizationsRaw
        .map((raw: unknown) => {
          if (!raw || typeof raw !== 'object') return null;
          const record = raw as Record<string, unknown>;
          const orgIdValue = normalizeText(record.orgId);
          if (!orgIdValue) return null;
          const roleValue = String(record.role ?? '').trim().toLowerCase();
          const roleNormalized: OrganizationRole =
            roleValue === 'owner' ? 'owner' : 'member';
          const orgTypeRaw = String(record.organizationType ?? 'individual')
            .trim()
            .toLowerCase();
          const orgType: 'individual' | 'business' =
            orgTypeRaw === 'business' ? 'business' : 'individual';
          const permissionsRecord =
            record.permissions && typeof record.permissions === 'object'
              ? (record.permissions as Record<string, unknown>)
              : {};

          return {
            orgId: orgIdValue,
            orgName: normalizeText(record.orgName) || 'Organization',
            organizationType: orgType,
            displayLabel:
              normalizeText(record.displayLabel) ||
              normalizeText(record.businessName) ||
              normalizeText(record.orgName) ||
              'Organization',
            businessName: normalizeText(record.businessName),
            logoURL: normalizeText(record.logoURL),
            avatarURL:
              normalizeText(record.avatarURL) || normalizeText(record.logoURL),
            role: roleNormalized,
            isPrimaryAccountHolder:
              record.isPrimaryAccountHolder === true || roleNormalized === 'owner',
            isActive: record.isActive === true,
            permissions: {
              inviteMembers: permissionsRecord.inviteMembers === true,
              createTickets: permissionsRecord.createTickets === true,
              manageBilling: permissionsRecord.manageBilling === true,
              manageSubscription: permissionsRecord.manageSubscription === true,
              manageOrgProfile: permissionsRecord.manageOrgProfile === true,
            },
          };
        })
        .filter(
          (value: OrganizationContext | null): value is OrganizationContext =>
            Boolean(value),
        );

      const activeOrgFromPayload =
        normalizeText(data?.activeOrgId)
        || normalizedOrganizations.find((org) => org.isActive)?.orgId
        || normalizeText(data?.primaryOrgId)
        || normalizedOrganizations[0]?.orgId
        || null;

      const activeOrganization =
        normalizedOrganizations.find((org) => org.orgId === activeOrgFromPayload)
        || null;
      const primaryAccountHolder =
        Boolean(data?.isPrimaryAccountHolder)
        || Boolean(activeOrganization?.isPrimaryAccountHolder)
        || activeOrganization?.role === 'owner';
      const profileComplete =
        typeof data?.businessProfileComplete === 'boolean'
          ? data.businessProfileComplete
          : activeOrganization?.organizationType === 'business';

      setOrganizations(
        normalizedOrganizations.map((org) => ({
          ...org,
          isActive: org.orgId === activeOrgFromPayload,
        })),
      );
      setActiveOrgId(activeOrgFromPayload);
      setBusinessProfileComplete(Boolean(profileComplete));
      setIsPrimaryAccountHolder(primaryAccountHolder);
      setRequiresInitialPasswordSetup(Boolean(data?.requiresInitialPasswordSetup));
      setRole(
        r === 'admin' || r === 'agent' || r === 'client' || r === 'candidate'
          ? r
          : null,
      );

      const orgIdsFromClaims = Array.isArray(data?.orgIds)
        ? data.orgIds
            .map((value: unknown) => (typeof value === 'string' ? value.trim() : ''))
            .filter((value: string) => Boolean(value))
        : [];
      const membershipExists = Boolean(
        normalizedOrganizations.length
        || activeOrgFromPayload
        || orgIdsFromClaims.length,
      );
      setHasOrganizationMembership(membershipExists);
      setThemeState(tmode === 'dark' ? 'dark' : 'light');
    } catch (err) {
      console.error('Failed to fetch user profile', err);
      setRole(null);
      setActiveOrgId(null);
      setOrganizations([]);
      setHasOrganizationMembership(false);
      setIsPaid(false);
      setBusinessProfileComplete(false);
      setIsPrimaryAccountHolder(false);
      setRequiresInitialPasswordSetup(false);
      setIsBanned(false);
      setBanReason(null);
      setThemeState('light');
    } finally {
      setLoad(false);
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          await startNotificationsRealtime();
        } catch (err) {
          console.warn('Realtime notifications failed to start:', err);
        }
        await ensureUserDoc(u);
      } else {
        stopNotificationsRealtime();
      }
      await fetchProfile(u);
    });
    return () => {
      stopNotificationsRealtime();
      unsub();
    };
  }, []);

  const refreshRole = async () => {
    const u = auth.currentUser ?? null;
    if (!u) return fetchProfile(null);
    try {
      const token = await u.getIdToken();
      await fetch(`${API_BASE}/api/users/refresh-claims`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      console.warn('Failed to refresh custom claims', err);
    }
    await u.getIdToken(true);
    await fetchProfile(u);
  };

  const setTheme = async (m: Mode) => {
    setThemeState(m);
    if (!auth.currentUser) return;
    try {
      const token = await auth.currentUser.getIdToken();
      await fetch(`${API_BASE}/api/users/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ theme: m }),
      });
    } catch (err) {
      console.error('Failed to save theme preference', err);
    }
  };

  const setActiveOrg = async (nextOrgId: string) => {
    const trimmed = nextOrgId.trim();
    if (!trimmed || !auth.currentUser) return;
    try {
      const token = await auth.currentUser.getIdToken();
      await fetch(`${API_BASE}/api/org-context/active`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orgId: trimmed }),
      });
      await refreshRole();
    } catch (error) {
      console.error('Failed to switch active organization', error);
      throw error;
    }
  };

  const logout = () => signOut(auth);

  return (
    <Context.Provider
      value={{
        user,
        role,
        activeOrgId,
        organizations,
        setActiveOrg,
        orgId: activeOrgId,
        hasOrganizationMembership,
        isPaid,
        businessProfileComplete,
        isPrimaryAccountHolder,
        requiresInitialPasswordSetup,
        isBanned,
        banReason,
        theme,
        loading,
        refreshRole,
        setTheme,
        logout,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export const useAuth = () => useContext(Context);
